// client/src/modules/crm/routes.tsx
import React, { useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { lazyWithRetry } from '../../lib/lazyWithRetry';
import { useAuth } from '../../contexts/AuthContext';

// Lazy load all CRM pages for code splitting
const LeadList = lazyWithRetry(() => import('../../pages/CRM/LeadList'));
const LeadDetail = lazyWithRetry(() => import('../../pages/CRM/LeadDetail'));
const OutboundDashboard = lazyWithRetry(() => import('../../pages/CRM/OutboundDashboard'));
// M&A deals live in the locked store (restricted_leads), not the outbound boards.
const MnaBoard = lazyWithRetry(() => import('../../pages/CRM/MnaBoard'));
const MnaDealDetail = lazyWithRetry(() => import('../../pages/CRM/MnaDealDetail'));
const AISalesAssistant = lazyWithRetry(() => import('../../pages/CRM/AISalesAssistant'));
const CRMSettings = lazyWithRetry(() => import('../../pages/CRM/Settings/CRMSettings'));
const PipelineAnalytics = lazyWithRetry(() => import('../../pages/CRM/PipelineAnalytics'));
const WonJobsAnalytics = lazyWithRetry(() => import('../../pages/CRM/WonJobsAnalytics'));
const ContactList = lazyWithRetry(() => import('../../pages/CRM/ContactList'));
const ContactDetail = lazyWithRetry(() => import('../../pages/CRM/ContactDetail'));
const SurveyList = lazyWithRetry(() => import('../../pages/Surveys/SurveyList'));
const ProposalFollowUps = lazyWithRetry(() => import('../../pages/CRM/ProposalFollowUps'));
const ModuleAgentFeed = lazyWithRetry(() =>
  import('../../components/shared/ModuleAgentFeed').then(m => ({ default: m.ModuleAgentFeed }))
);

const VALID_TABS = ['leads', 'contacts', 'surveys', 'follow-ups', 'outbound', 'ai-assistant', 'analytics', 'settings'];
const VALID_OUTBOUND_TABS = ['b2b', 'bizdev', 'mna'];

/** Redirect to user's preferred CRM tab (localStorage) or default to leads */
function CRMDefaultRedirect() {
  const savedTab = localStorage.getItem('crm-default-tab') || 'leads';
  const tab = VALID_TABS.includes(savedTab) ? savedTab : 'leads';
  return <Navigate to={tab} replace />;
}

/** Wrapper that persists the current CRM tab to localStorage and renders children */
function PersistTab({ tab, children }: { tab: string; children: React.ReactNode }) {
  useEffect(() => {
    if (VALID_TABS.includes(tab)) {
      localStorage.setItem('crm-default-tab', tab);
    }
  }, [tab]);
  return <>{children}</>;
}

/** Resolves the user's last-active outbound tab from localStorage, or 'b2b' */
function resolveDefaultOutboundTab(userId: string): string {
  const key = `outbound:lastTab:${userId}`;
  const stored = localStorage.getItem(key);
  if (stored && VALID_OUTBOUND_TABS.includes(stored)) return stored;
  return 'b2b';
}

/**
 * Redirects bare /crm/outbound to the user's last-active outbound tab (from localStorage)
 * or defaults to b2b.
 */
function OutboundDefaultRedirect() {
  const { firebaseUser } = useAuth();
  const defaultTab = firebaseUser?.uid
    ? resolveDefaultOutboundTab(firebaseUser.uid)
    : 'b2b';
  return <Navigate to={defaultTab} replace />;
}

/**
 * Validates the :tab param — invalid values redirect to /crm/outbound/b2b.
 * Valid tabs are rendered inside PersistTab so the CRM sidebar highlights "outbound".
 */
function OutboundTabRoute() {
  const { tab } = useParams<{ tab: string }>();
  if (!tab || !VALID_OUTBOUND_TABS.includes(tab)) {
    return <Navigate to="/crm/outbound/b2b" replace />;
  }
  return (
    <PersistTab tab="outbound">
      <ProtectedRoute>
        {tab === 'mna' ? <MnaBoard /> : <OutboundDashboard />}
      </ProtectedRoute>
    </PersistTab>
  );
}

/**
 * CRM module routes - relative to /crm prefix
 */
export const routes: RouteObject[] = [
  {
    index: true,
    element: <CRMDefaultRedirect />,
  },
  {
    path: 'leads',
    element: <PersistTab tab="leads"><ProtectedRoute><LeadList /></ProtectedRoute></PersistTab>,
  },
  {
    path: 'leads/:leadId',
    element: <PersistTab tab="leads"><ProtectedRoute><LeadDetail /></ProtectedRoute></PersistTab>,
  },
  {
    path: 'outbound',
    element: <OutboundDefaultRedirect />,
  },
  {
    path: 'outbound/:tab',
    element: <OutboundTabRoute />,
  },
  {
    path: 'outbound/mna/deals/:dealId',
    element: <PersistTab tab="outbound"><ProtectedRoute><MnaDealDetail /></ProtectedRoute></PersistTab>,
  },
  {
    path: 'ai-assistant',
    element: <PersistTab tab="ai-assistant"><ProtectedRoute><AISalesAssistant /></ProtectedRoute></PersistTab>,
  },
  {
    path: 'settings',
    element: <PersistTab tab="settings"><ProtectedRoute><CRMSettings /></ProtectedRoute></PersistTab>,
  },
  {
    path: 'analytics/won',
    element: <PersistTab tab="analytics"><ProtectedRoute><WonJobsAnalytics /></ProtectedRoute></PersistTab>,
  },
  {
    path: 'analytics',
    element: <PersistTab tab="analytics"><ProtectedRoute><PipelineAnalytics /></ProtectedRoute></PersistTab>,
  },
  {
    path: 'contacts',
    element: <PersistTab tab="contacts"><ProtectedRoute><ContactList /></ProtectedRoute></PersistTab>,
  },
  {
    path: 'contacts/:contactId',
    element: <PersistTab tab="contacts"><ProtectedRoute><ContactDetail /></ProtectedRoute></PersistTab>,
  },
  {
    path: 'surveys',
    element: <PersistTab tab="surveys"><ProtectedRoute><SurveyList /></ProtectedRoute></PersistTab>,
  },
  {
    path: 'follow-ups',
    element: <PersistTab tab="follow-ups"><ProtectedRoute><ProposalFollowUps /></ProtectedRoute></PersistTab>,
  },
  {
    path: 'agent-activity',
    element: <ProtectedRoute><ModuleAgentFeed moduleId="P1" /></ProtectedRoute>,
  },
  {
    // ABSOLUTE, deliberately. These entries mount as CHILD routes under /crm,
    // so a relative `to="leads"` resolved against whatever unknown URL the user
    // landed on and appended to it forever: measured 2026-09-23, /crm/about (a
    // live bookmark — the About pages were removed in #4984) became
    // /crm/about/leads/leads/leads/... and rendered no module content.
    // scripts/guards/check-module-catchall-absolute.mjs keeps this absolute.
    path: '*',
    element: <Navigate to="/crm/leads" replace />,
  },
];
